import React from 'react';
import autobind from 'core-decorators/lib/autobind';

import WixSDK from 'shared/utils/wix-sdk';

import attachScript from '@wix/wix-vod-shared/src/common/utils/attach-script';
import PropTypes from 'prop-types';
import { stringify } from '@wix/wix-vod-shared/src/common/utils/query-string';


const locale = WixSDK.Utils.getLocale();


const scriptApiUrl = 'https://www.youtube.com/iframe_api';
let scriptPromise = null;


export default class YoutubeExternal extends React.Component {

  static propTypes = {
    src: PropTypes.string.isRequired,

    onPlay: PropTypes.func,
    onPause: PropTypes.func,
    onEnd: PropTypes.func,
    onIframeRef: PropTypes.func.isRequired,

    width: PropTypes.number,
    height: PropTypes.number,
    className: PropTypes.string,

    simple: PropTypes.bool,
    mobileMode: PropTypes.bool,

    isVideoPlayRequested: PropTypes.bool,
    isVideoPauseRequested: PropTypes.bool,
    isVideoPlaying: PropTypes.bool,
    isVideoPaused: PropTypes.bool,

    loop: PropTypes.bool,
    muted: PropTypes.bool,
  };

  constructor(props) {
    super(props);

    this.player = null;
    this.iframeRef = null;

    this.state = {
      isReady: false,
    };
  }

  componentDidMount() {
    window.onYouTubeIframeAPIReady = this.onYouTubeIframeAPIReady;

    // load once
    if (this.isScriptLoaded) {
      this.onYouTubeIframeAPIReady();
    } else if (!scriptPromise) {
      scriptPromise = attachScript(scriptApiUrl);
    }
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.src === this.props.src) {
      this.setSettings(nextProps, this.props);
    } else {
      this.setState({ isReady: false });
    }
  }

  componentWillUnmount() {
    window.onYouTubeIframeAPIReady = null;
  }

  @autobind
  saveIframeRef(ref) {
    this.iframeRef = ref;
    this.props.onIframeRef(ref);
  }

  get isScriptLoaded() {
    return _.has(window, 'YT.Player');
  }

  @autobind
  onYouTubeIframeAPIReady() {
    void new window.YT.Player(this.iframeRef, {
      events: {
        onReady: this.onReady,
        onStateChange: this.onStateChange,
      },
    });
  }

  @autobind
  onReady({ target }) {
    this.player = target;
    this.setState({ isReady: true });
    this.setSettings(this.props);
  }

  @autobind
  onStateChange({ data }) {
    const { onPlay, onPause, onEnd } = this.props;
    switch (data) {
      case window.YT.PlayerState.PLAYING:
        onPlay();
        break;
      case window.YT.PlayerState.PAUSED:
        onPause();
        break;
      case window.YT.PlayerState.ENDED:
        onEnd();
        break;
      default: break;
    }
  }

  setSettings({ isVideoPlayRequested, isVideoPauseRequested, loop, muted }, prevProps = {}) {
    if (!this.player || !this.state.isReady) {
      return null;
    }

    if (prevProps.isVideoPlayRequested !== isVideoPlayRequested && isVideoPlayRequested) {
      this.togglePaused(false);
    }

    if (prevProps.isVideoPauseRequested !== isVideoPauseRequested && isVideoPauseRequested) {
      this.togglePaused(true);
    }

    this.toggleLoop(loop);
    this.toggleMute(muted);
  }

  togglePaused(paused) {
    if (paused) {
      this.player.pauseVideo();
    } else {
      this.player.playVideo();
    }
  }

  toggleLoop(loop) {
    if (loop) {
      const { video_id } = this.player.getVideoData();
      // need load playlist to set loop to true
      // but loadPlay list sets video to the beginning
      this.player.loadPlaylist(video_id);
      this.player.setLoop(true);
    } else {
      this.player.setLoop(false);
    }
  }

  toggleMute(muted) {
    if (muted) {
      this.player.mute();
    } else {
      this.player.unMute();
    }
  }

  buildUrl({ src, simple, mobileMode, showControls }) {
    const { protocol, hostname, port } = window.location;

    const origin = `${protocol}//${hostname}${port ? ':' : ''}${port}`;

    const query = {
      enablejsapi: '1',
      hl: locale,
      rel: '0',
      origin,
    };

    if (simple || mobileMode) {
      query.showinfo = '0';
    }
    if (mobileMode) {
      query.playsinline = '0';
    }
    if (!showControls) {
      query.controls = '0';
    }

    return `${src}${stringify(query)}`;
  }

  render() {
    const props = _.pick(this.props, 'className', 'width', 'height');
    const { isVideoPauseRequested, isVideoPlayRequested, isVideoPlaying, isVideoPaused } = this.props;
    const tabIndex = isVideoPauseRequested || isVideoPlayRequested || isVideoPlaying || isVideoPaused ? 0 : -1;

    return (
      <iframe {...props}
              src={this.buildUrl(this.props)}
              frameBorder="0"
              allowFullScreen
              tabIndex={tabIndex}
              ref={this.saveIframeRef} />
    );
  }
}



// WEBPACK FOOTER //
// ./src/widget/player-widget/components/new-player/external/youtube.js